/**
 * PasskeyEnrollPrompt.jsx — Post-login modal that offers to set up a passkey
 * when the signed-in account doesn't have one yet.
 *
 * Shows at most once per-account per-session (sessionStorage flag) so it
 * doesn't hound users who dismissed it.
 */
import React, { useEffect, useState } from 'react';
import { KeyRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  isPasskeySupported,
  enrollPasskey,
  listPasskeys,
} from '../utils/passkeyApi';

const SESSION_KEY = (email) => `passkey-prompt-seen:${email || 'anon'}`;

const detectDefaultName = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/iPhone|iPad/.test(ua)) return 'iPhone (Face/Touch ID)';
  if (/Mac/.test(ua)) return 'Mac (Touch ID)';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua)) return 'Windows (Hello)';
  return 'This device';
};

const PasskeyEnrollPrompt = ({ email, onClose, auto = true }) => {
  const [open, setOpen] = useState(!auto);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auto) return;
    // If the modal isn't going to render (unsupported device, dismissed
    // earlier in this session, account already has a passkey, or the
    // listPasskeys lookup blew up), we MUST still call onClose so the
    // parent navigates the user away from the login screen. Otherwise
    // the user gets stranded after a successful login/registration —
    // which is the post-signup "stuck" bug previous agents missed.
    const skipAndContinue = () => {
      try {
        sessionStorage.setItem(SESSION_KEY(email), '1');
      } catch { /* ignore */ }
      onClose?.();
    };

    if (!isPasskeySupported()) {
      skipAndContinue();
      return;
    }
    let cancelled = false;
    let resolved = false;
    (async () => {
      // Skip if already dismissed/enrolled in this session for this account.
      try {
        if (sessionStorage.getItem(SESSION_KEY(email))) {
          resolved = true;
          if (!cancelled) onClose?.();
          return;
        }
      } catch {
        /* ignore */
      }
      try {
        const d = await listPasskeys();
        if (cancelled) return;
        resolved = true;
        if (!d.passkeys || d.passkeys.length === 0) {
          setOpen(true);
        } else {
          // Already has a passkey — nothing to prompt; let parent navigate.
          skipAndContinue();
        }
      } catch {
        // Lookup failed (eg. unauth, network). Don't block the user — let
        // them through. They can enrol later from Settings.
        if (cancelled) return;
        resolved = true;
        skipAndContinue();
      }
    })();
    return () => {
      cancelled = true;
      // Safety net — if the async block never resolved (e.g. unmount
      // mid-flight), still nudge the parent forward.
      if (!resolved) {
        try { onClose?.(); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, email]);

  const dismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY(email), '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
    onClose?.();
  };

  const handleEnroll = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading('Waiting for biometric / device unlock...');
    try {
      await enrollPasskey(detectDefaultName());
      toast.success('✅ Passkey saved. You can sign in with a tap next time.', { id: t });
      try { window.dispatchEvent(new CustomEvent('passkey:changed')); } catch { /* ignore */ }
      dismiss();
    } catch (e) {
      const msg = /NotAllowedError|cancell/i.test(e.message || '')
        ? 'Cancelled.'
        : (e.message || 'Enrolment failed');
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="passkey-enroll-prompt"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-slate-900 border border-fuchsia-500/30 rounded-2xl shadow-2xl overflow-hidden">
        <div className="relative p-6 bg-gradient-to-br from-fuchsia-600/20 via-purple-600/10 to-slate-900">
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:bg-slate-800 hover:text-white"
            aria-label="Dismiss"
            data-testid="passkey-enroll-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-14 h-14 rounded-xl bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center mb-4">
            <KeyRound className="w-7 h-7 text-fuchsia-300" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Skip passwords next time</h2>
          <p className="text-sm text-gray-300">
            Add a passkey and sign in with Face ID, Touch ID or your device PIN —
            safer than a password, and about <span className="font-semibold text-fuchsia-200">one second</span> to log in.
          </p>
        </div>
        <div className="p-6 pt-4 space-y-3">
          <Button
            onClick={handleEnroll}
            disabled={busy}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold py-6"
            data-testid="passkey-enroll-confirm"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {busy ? 'Setting up...' : 'Add a passkey'}
          </Button>
          <button
            type="button"
            onClick={dismiss}
            className="w-full text-xs text-gray-400 hover:text-gray-200"
            data-testid="passkey-enroll-skip"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasskeyEnrollPrompt;
